import { apiClient } from "@/src/api/apiClient";
import { AUTH_ENDPOINTS } from "@/src/api/endpoints";
import type {
  AppleAuthPayload,
  ChangePasswordPayload,
  ForgotPasswordPayload,
  GoogleAuthPayload,
  HomeownerUser,
  LoginPayload,
  LoginResponseData,
  RefreshTokenPayload,
  RequestLoginOtpPayload,
  RequestLoginOtpResponse,
  ResendVerificationPayload,
  ResetPasswordPayload,
  SignupPayload,
  SignupResponse,
  UpdateProfilePayload,
  VerifyEmailPayload,
  VerifyEmailResponseData,
} from "@/src/types/auth.types";

export const authService = {
  signup(payload: SignupPayload): Promise<SignupResponse> {
    return apiClient.post<SignupResponse>(AUTH_ENDPOINTS.SIGNUP, payload, { skipAuth: true });
  },

  /** @deprecated Prefer requestLoginOtp */
  login(payload: LoginPayload): Promise<LoginResponseData> {
    return apiClient.post<LoginResponseData>(AUTH_ENDPOINTS.LOGIN, payload, { skipAuth: true });
  },

  requestLoginOtp(payload: RequestLoginOtpPayload): Promise<RequestLoginOtpResponse> {
    return apiClient.post<RequestLoginOtpResponse>(AUTH_ENDPOINTS.REQUEST_LOGIN_OTP, payload, {
      skipAuth: true,
      skipGlobalErrorHandler: true,
    });
  },

  googleLogin(payload: GoogleAuthPayload): Promise<LoginResponseData> {
    return apiClient.post<LoginResponseData>(AUTH_ENDPOINTS.GOOGLE, payload, { skipAuth: true });
  },

  appleLogin(payload: AppleAuthPayload): Promise<LoginResponseData> {
    return apiClient.post<LoginResponseData>(AUTH_ENDPOINTS.APPLE, payload, { skipAuth: true });
  },

  verifyOtp(payload: VerifyEmailPayload): Promise<VerifyEmailResponseData> {
    return apiClient.post<VerifyEmailResponseData>(AUTH_ENDPOINTS.VERIFY_OTP, payload, {
      skipAuth: true,
    });
  },

  resendOtp(payload: ResendVerificationPayload): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>(AUTH_ENDPOINTS.RESEND_OTP, payload, {
      skipAuth: true,
    });
  },

  forgotPassword(payload: ForgotPasswordPayload): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>(AUTH_ENDPOINTS.FORGOT_PASSWORD, payload, {
      skipAuth: true,
    });
  },

  resetPassword(payload: ResetPasswordPayload): Promise<{ updated: boolean }> {
    return apiClient.post<{ updated: boolean }>(AUTH_ENDPOINTS.RESET_PASSWORD, payload, {
      skipAuth: true,
    });
  },

  changePassword(
    payload: ChangePasswordPayload,
    accessToken: string,
  ): Promise<{ updated: boolean }> {
    return apiClient.patch<{ updated: boolean }>(AUTH_ENDPOINTS.CHANGE_PASSWORD, payload, {
      tokenHeader: accessToken,
      suppressUnauthorizedRedirect: true,
    });
  },

  logout(payload: RefreshTokenPayload): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>(AUTH_ENDPOINTS.LOGOUT, payload);
  },

  getProfile(): Promise<HomeownerUser> {
    return apiClient.get<HomeownerUser>(AUTH_ENDPOINTS.PROFILE);
  },

  updateProfile(payload: UpdateProfilePayload): Promise<HomeownerUser> {
    return apiClient.patch<HomeownerUser>(AUTH_ENDPOINTS.UPDATE_PROFILE, payload);
  },

  refreshToken(payload: RefreshTokenPayload): Promise<{
    accessToken: string;
    refreshToken: string;
    expiresIn?: number;
  }> {
    return apiClient.post(AUTH_ENDPOINTS.REFRESH, payload, { skipAuth: true });
  },
};
